import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import { TCAppConfig } from "../../../../../../shared/app-configs/tc-app-configs";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import FormBuilder from "../../../../../../shared/components/form-builder/form-builder";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../shared/components/inputfields/form-elements";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { useApi } from "../../../../../../shared/hooks/useApi";
import { IInputTcDetails } from "../../../../tc-models/tc-req-dto/input-tc-product.req_dto";
import { Option } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { buyerSellerDetailsService } from "../../../../tc-service/buyer-seller-details.service";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { useFormik } from "formik";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { Box, Typography } from "@mui/material";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../shared/hooks/useNotification";
import _ from "lodash";

interface InputTcDetailsProps {
  formRef: any;
  inputData: IInputTcDetails;
  ocsProcessor: any;
  setInputData: React.Dispatch<React.SetStateAction<IInputTcDetails>>;
}

const InputTcDetails: React.FC<InputTcDetailsProps> = ({
  formRef,
  inputData,
  ocsProcessor,
  setInputData,
}) => {
  const { TE_ID, formtype } = useParams<string>();
  const [product, setProduct] = useState<Option[]>([]);
  const [productDetails, setProductDetails] = useState<any[]>([]);
  const [selectedProductRawMaterialCodes, setSelectedProductRawMaterialCodes] = useState<string[]>([]);
  const [selectedAssociatedProductNo, setSelectedAssociatedProductNo] = useState<string>(
    inputData?.tcAssociatedProductNo || ""
  );
  const { executeGet } = useApi();
  const [cbLicensingCodes, setLicensingCodes] = useState<Option[]>([]);
  const hasCreateTc: boolean = formtype === "create-tc";
  const [getTcUSDAComplianceFlag, setTcUSDAComplianceFlag] =
    useState<boolean>(false);
  const [rawMaterialCodes, setRawMaterialCodes] = useState<any[]>([]);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [data, setData] = useState<any>(
    inputData?.inputTcCertifiedRawMaterialWeightUsedInformation ?? []
  );
  const [tableData, setTableData] = useState<any>(
    inputData?.inputTcCertifiedRawMaterialWeightUsedInformation ?? []
  );
  const [isTELabelEnabled, setTELabelEnabled] = useState(
    !inputData?.inputTcSellerTeId || inputData?.inputTcSellerTeId === ""
  );
  const { isRestrictPopUpSave, setRestrictPopUpSave } =
    useTransactionCertificateStore();
  const { notify } = useNotification();
  const [isInputTcNoNA, setIsInputTcNoNA] = useState(
    inputData?.inputTcNo === "N/A"
  );

  const fetchStandardDetails = async () => {
    const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
      tcId: TE_ID,
    });
    if (response?.data) {
      const isOCSSelected = response?.data?.tcStandard.includes("OCS")
        ? true
        : false;
      setTcUSDAComplianceFlag(isOCSSelected);
    }
  };
  const fetchProductDetails = async () => {
    try {
      const response: any = await executeGet(
        TCAppConfig.GET_PRODUCT_RAWMATERIAL_INFO_LIST,
        { tcId: TE_ID }
      );

      // Save the full response data for later use
      const fullResponseData = response?.data?.response || [];
      setProductDetails(fullResponseData);

      // Map over the array in the "response" key to extract the tcProductNo property
      const result: any[] =
        fullResponseData.map((detail: { tcProductNo?: string }) => ({
          value: detail.tcProductNo ?? "",
          label: detail.tcProductNo ?? "",
        })) || [];

      const uniqueData = result.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.value === item.value)
      );
      setProduct(uniqueData);
    } catch (err) {
      console.error("Failed to fetch Base Details:", err);
    }
  };

  const handleAssociatedProductNoChange = (selectedValue: string) => {
    // Save the selected associated product number in state
    setSelectedAssociatedProductNo(selectedValue);
    
    // Find the selected product details
    const selectedProduct = productDetails.find(
      (product: any) => product.tcProductNo === selectedValue
    );
    
    // Extract and save the tcProductRawMaterialCode for the selected product
    if (selectedProduct && selectedProduct.tcProductRawMaterialCode) {
      setSelectedProductRawMaterialCodes(selectedProduct.tcProductRawMaterialCode);
    } else {
      setSelectedProductRawMaterialCodes([]);
    }

    // Reset output fields when product changes
    setData((prevData: any[]) => {
      return prevData.map((row: any) => {
        const updatedRow = { ...row };
        
        // If the current input code is not in the new selected codes, reset the output
        if (row.inputTcCertifiedRawMaterialCode && 
            !selectedProduct?.tcProductRawMaterialCode?.includes(row.inputTcCertifiedRawMaterialCode)) {
          updatedRow.outputTcCertifiedRawMaterialCode = "";
        }
        
        return updatedRow;
      });
    });
  };
  const getCBLicensingCodes = async () => {
    const result: any = await dtrackItService.getCBLicensingCodes();
    // Utills.cl(`result getCBLicensingCodes ${JSON.stringify(result)}`)
    const cbLicensing = result.data.map((t: any) => ({
      value: t.code_1,
      label: `${t.description_1} (${t.code_1})`,
    }));
    // Utills.cl(`cbLicensing ${JSON.stringify(cbLicensing)}`)
    setLicensingCodes(cbLicensing);
  };
  useEffect(() => {
    inputData.inputTcNo = ocsProcessor.length > 0 ? "N/A" : inputData.inputTcNo;
    // Ensure UI state reflects programmatic changes to Input TC No
    setIsInputTcNoNA(inputData.inputTcNo === "N/A");
    // //alert(`input-tc-details ${JSON.stringify(inputData)}`)
    getCBLicensingCodes();
    if (TE_ID) {
      fetchProductDetails();
      fetchStandardDetails();
    }
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();


        const rawMaterials = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
          description: t.description_1,
        }));
        setRawMaterialCodes(rawMaterials);
      } catch (err) {
        console.log(`Error in Fetching Organic Farm Standard Code ${err}`);
      }
    };

    getRawMaterialCodes();
  }, []);

  // Handle initialization of selected product raw material codes when component loads with existing data
  useEffect(() => {
    if (selectedAssociatedProductNo && productDetails.length > 0) {
      const selectedProduct = productDetails.find(
        (product: any) => product.tcProductNo === selectedAssociatedProductNo
      );
      
      if (selectedProduct && selectedProduct.tcProductRawMaterialCode) {
        setSelectedProductRawMaterialCodes(selectedProduct.tcProductRawMaterialCode);
      }
    }
  }, [selectedAssociatedProductNo, productDetails]);

  // Update table data when inputData changes (for editing existing records)
  useEffect(() => {
    if (inputData?.inputTcCertifiedRawMaterialWeightUsedInformation) {
      setData(inputData.inputTcCertifiedRawMaterialWeightUsedInformation);
      setTableData(inputData.inputTcCertifiedRawMaterialWeightUsedInformation);
    }
  }, [inputData?.inputTcCertifiedRawMaterialWeightUsedInformation]);

  // Update table data when inputData changes (for navigation between steps)
  useEffect(() => {
    if (inputData?.inputTcCertifiedWeightUsed) {
      setData(inputData.inputTcCertifiedWeightUsed);
      setTableData(inputData.inputTcCertifiedWeightUsed);
    }
  }, [inputData?.inputTcCertifiedWeightUsed]);

  // Update selected associated product number when editing existing records
  useEffect(() => {
    if (inputData?.tcAssociatedProductNo) {
      setSelectedAssociatedProductNo(inputData.tcAssociatedProductNo);
    }
  }, [inputData?.tcAssociatedProductNo]);

  // Comprehensive effect to handle all inputData changes
  useEffect(() => {
    // Update table data from either source
    if (inputData?.inputTcCertifiedRawMaterialWeightUsedInformation) {
      setData(inputData.inputTcCertifiedRawMaterialWeightUsedInformation);
      setTableData(inputData.inputTcCertifiedRawMaterialWeightUsedInformation);
    } else if (inputData?.inputTcCertifiedWeightUsed) {
      setData(inputData.inputTcCertifiedWeightUsed);
      setTableData(inputData.inputTcCertifiedWeightUsed);
    }
    
    // Update other form-related states
    if (inputData?.inputTcNo) {
      setIsInputTcNoNA(inputData.inputTcNo === "N/A");
    }
    
    if (inputData?.inputTcSellerTeId) {
      setTELabelEnabled(!inputData.inputTcSellerTeId || inputData.inputTcSellerTeId === "");
    }
  }, [inputData]);
  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      // Fetch TE Details using the getTEDetails function
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;
      Utills.cl(
        `fetchTEDetallsFromAPI details, query ${JSON.stringify(
          queryParams
        )} response data ${JSON.stringify(data)}`
      );

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };

  const handleSellerTEIdValueChange = (value: any) => {
    if (value == null) {
      formRef.current.setFieldValue("inputTcSellerTeId", "");
      setTELabelEnabled(true);
    } else {
      formRef.current.setFieldValue("inputTcSellerTeId", value.TE_ID);
      setTELabelEnabled(false);
    }
  };
  const formConfig = {
    pageName: "input-tc-product-details",
    initialValues: inputData,
    validationSchema: Yup.object().shape({
      InputCBLicensingCode: Yup.string(),
      inputTcNo: Yup.string()
        .required("TC No is required")
        .max(100, "Maximum 100 Character is allowed"),
      tcAssociatedProductNo: Yup.string().required(
        "Associated Product No. is required"
      ),
      inputTcSellerLicenseNo: Yup.string().nullable(),
      inputTcSellerScNo: Yup.string().nullable(),
      inputTcSellerTeId: Yup.string().nullable(),
      inputTcUsdaCompliantFlag: Yup.string().nullable(),
      inputTcCertifiedWeightUsed: Yup.array().nullable(),
    }),
    sections: [
      {
        title: "Input Details",
        fields: [
          {
            label: "Associated Product No.",
            name: "tcAssociatedProductNo",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            value: "",
            props: {
              options: product,
              placeholder: "Select Product No.",
              variant: "outlined",
              fullWidth: true,
              onChange: (e: any) => {
                formRef.current.setFieldValue("tcAssociatedProductNo", e.target.value);
                handleAssociatedProductNoChange(e.target.value);
              },
            },
          },
          {
            label: "Input TC No.",
            name: "inputTcNo",
            type: "text",
            disabled: ocsProcessor.length > 0 ? true : false,
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: true,
            props: {
              placeholder: "Enter Input TC No",
              onBlur: (e: any) => {
                // Update state when field loses focus
                setIsInputTcNoNA(e.target.value === "N/A");
              },
            },
          },
          {
            label: "Input CB Licensing Code",
            name: "InputCBLicensingCode",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              enableSearch: true,
              options: cbLicensingCodes,
              placeholder: "Select CB Name",
            },
          },
          {
            label: "Seller License No.", //'Input Seller License No..', renamed to match tbl hdr
            name: "inputTcSellerLicenseNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Enter Seller License No.",
            },
          },
          {
            label: "Seller SC No.", //renamed to match table hdr was Input Seller SC No.
            name: "inputTcSellerScNo",
            type: "text",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Enter Seller SC No.",
            },
          },
          {
            label: "Input TC Seller TE-ID", //renamed to match table header
            name: "inputTcSellerTeId",
            type: "search",
            gridSize: { xs: 12, md: 4, lg: 4 },
            required: false,
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              param: "inputTcSellerTeId",
              restrictInputFormat: isTELabelEnabled,
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) => {
                Utills.cl(
                  `fetchTEDetallsFromAPI query ${query} offset ${offset}`
                );
                const response = fetchTEDetallsFromAPI(query, offset, "query");
                return response;
              },
              fullWidth: true,
            },
          },
          {
            label: "USDA Compliance", //renamed to match table header
            name: "inputTcUsdaCompliantFlag",
            type: "select",
            gridSize: { xs: 12, md: 4, lg: 4 },
            visible: (values: any) => getTcUSDAComplianceFlag,
            required: false,
            props: {
              options: [
                { value: "Y", label: "Yes" },
                { value: "N", label: "No" },
              ],
              placeholder: "Select Compliance",
            },
          },
          // {
          //   label: 'Input Certified Weight Used (e.g format: RM0064-1000)',
          //   name: 'inputTcCertifiedWeightUsed',
          //   type: 'chips',
          //   gridSize: { xs: 12, md: 12, lg: 12 },
          //   required: false,
          //   props: {
          //     placeholder: 'Enter Certified Weight',
          //     helpText: "once value is entered please click Enter button to add"
          //   },
          // },
        ],
      },
    ],
         handleSubmit: (values: any) => {
       Utills.cl("Form Submitted:", formRef);
       if (!data || _.isEmpty(data)) {
         notify("Please add certified raw materials details", "error");
         return false;
       } else {
         const updatedData = { 
           ...values, 
           inputTcCertifiedWeightUsed: data,
           inputTcCertifiedRawMaterialWeightUsedInformation: data 
         };
         setInputData(updatedData);
       }
     },
  };

  const columns: EditableColumnDef<any>[] = [
    {
      accessorKey: "inputTcCertifiedRawMaterialCode",
      header: "Input Certified Raw Material Code",
      Header: (
        <span>
          {!isInputTcNoNA && <i style={{ color: "red" }}>* </i>}{" "}
          Input Certified Raw Material Code
        </span>
      ),
      enableHiding: false,
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          label=""
          name="inputTcCertifiedRawMaterialCode"
          enableSearch={true}
          value={value}
          options={rawMaterialCodes}
          required={!isInputTcNoNA}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        />
      ),
      minSize: 60,
      maxSize: 150,
    },
         {
       accessorKey: "outputTcCertifiedRawMaterialCode",
       header: "Output Certified Raw Material Code",
       Header: (
        <span>
          {" "}
          <i style={{ color: "red" }}>* </i> Output Certified Raw Material Code
        </span>
      ),
       enableHiding: false,
       renderEditCell: ({ value, onChange }) => {
         // Filter rawMaterialCodes to only include selected product codes
         const filteredOptions = rawMaterialCodes.filter((option: any) => 
           selectedProductRawMaterialCodes.includes(option.value)
         );
         
         return (
           <CustomSelect
             label=""
             name="outputTcCertifiedRawMaterialCode"
             enableSearch={true}
             value={value}
             options={filteredOptions}
             onChange={(e) => {
               onChange(e.target.value);
             }}
           />
         );
       },
       minSize: 60,
       maxSize: 150,
     },
    {
      accessorKey: "inputTcCertifiedRawMaterialWeightUsed",
      header: "Input Certified Raw Material Weight Used (kg)",
      Header: (
        <span>
          {!isInputTcNoNA && <i style={{ color: "red" }}>* </i>}{" "}
          Input Certified Raw Material Weight Used (kg)
        </span>
      ),
      enableHiding: false,
      minSize: 60,
      maxSize: 150,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTcCertifiedRawMaterialWeightUsed"
          value={value}
          required={!isInputTcNoNA}
          inputProps={{
            max: 100,
            step: "0.01",
          }}
          type="number"
          onChange={(e) => {
            const newValue = e.target.value;
            // Only allow numbers with max 2 decimal places
            const regex = /^\d*\.?\d{0,2}$/;
            if (regex.test(newValue) || newValue === "") {
              onChange(newValue);
            }
          }}
          placeholder="Enter value"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  const formik = useFormik<any>({
    initialValues: {
      inputTcCertifiedRawMaterialCode: "",
      outputTcCertifiedRawMaterialCode: "",
      inputTcCertifiedRawMaterialWeightUsed: null,
    },
    validationSchema: Yup.object().shape({
      inputTcCertifiedRawMaterialCode: Yup.string().required(),
      outputTcCertifiedRawMaterialCode: Yup.string().required(),
      inputTcCertifiedRawMaterialWeightUsed: Yup.number().required(),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);

    setData((prevData: any[]) => {
      const updatedData = prevData.map((row, index) => {
        if (index === rowIndex) {
          const updatedRow = { ...row, [columnId]: value };
          
          // If inputTcCertifiedRawMaterialCode is being changed
          if (columnId === "inputTcCertifiedRawMaterialCode") {
            if (selectedProductRawMaterialCodes.includes(value)) {
              // Auto-populate outputTcCertifiedRawMaterialCode with the same value
              updatedRow.outputTcCertifiedRawMaterialCode = value;
              formik.setFieldValue("outputTcCertifiedRawMaterialCode", value);
            } else {
              // Clear output if the input code is not in the selected codes
              updatedRow.outputTcCertifiedRawMaterialCode = "";
              formik.setFieldValue("outputTcCertifiedRawMaterialCode", "");
            }
          }
          
          return updatedRow;
        }
        return row;
      });
      
      return updatedData;
    });
  };

  const handleAddNewRow = () => {
    const newRow: any = {
      inputTcCertifiedRawMaterialCode: "",
      outputTcCertifiedRawMaterialCode: "",
      inputTcCertifiedRawMaterialWeightUsed: null,
      inputTcCertifiedRawMaterialCodeDescription: "",
      outputTcCertifiedRawMaterialCodeDescription: "",
    };
    setData((prevData: any) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    if (editingRowIndex !== null) {
      // const isLastRow = editingRowIndex === data.length-1;
      // const currentRowValues = formik.values;
      // const isEmptyRow = Object.values(currentRowValues).every(
      //   (value) => !value || value === "" || (Array.isArray(value) && value.length === 0)
      // );

      // if (isLastRow && isEmptyRow) {
      //   setData((prevData: string | any[]) => prevData.slice(0, -1));
      // } else {
      //   formik.setValues(data[editingRowIndex]);
      // }
      setData(tableData);
    }
    setEditingRowIndex(null);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    const currentRowValues = formik.values;
    
    let isValid = true;
    let errors: any = {};

    // Only validate these fields if inputTcNo is not "N/A"
    if (!isInputTcNoNA) {
      if (!currentRowValues.inputTcCertifiedRawMaterialCode) {
        errors.inputTcCertifiedRawMaterialCode = "Input Certified Raw Material Code is required";
        isValid = false;
      }
      if (!currentRowValues.inputTcCertifiedRawMaterialWeightUsed) {
        errors.inputTcCertifiedRawMaterialWeightUsed = "Input Certified Raw Material Weight Used is required";
        isValid = false;
      }
      if (!currentRowValues.outputTcCertifiedRawMaterialCode) {
        errors.outputTcCertifiedRawMaterialCode = "Output Certified Raw Material Code is required";
        isValid = false;
      }
    }

    // When Input TC No is "N/A", output code is also optional

    if (!isValid) {
      formik.setErrors(errors);
      return;
    }

    const updatedRow = {
      ...currentRowValues,
      inputTcCertifiedRawMaterialCodeDescription: rawMaterialCodes?.find(
        (codes: any) =>
          codes?.value === currentRowValues?.inputTcCertifiedRawMaterialCode
      )?.description,
      outputTcCertifiedRawMaterialCodeDescription: rawMaterialCodes?.find(
        (codes: any) =>
          codes?.value === currentRowValues?.outputTcCertifiedRawMaterialCode
      )?.description,
    };
    const newData = data.map((row: any, index: number) =>
      index === editingRowIndex ? updatedRow : row
    );
    setData(newData);
    setTableData(newData);
    setEditingRowIndex(null);
    setRestrictPopUpSave(false);
         setInputData({
       ...formRef?.current?.values,
       inputTcCertifiedWeightUsed: newData,
       inputTcCertifiedRawMaterialWeightUsedInformation: newData,
     });

    formik.resetForm();
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setRestrictPopUpSave(true);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter(
      (_: any, index: number) => index !== rowIndex
    );
    setData(updatedData);
    setTableData(updatedData);
         setInputData({
       ...formRef?.current?.values,
       inputTcCertifiedWeightUsed: updatedData,
       inputTcCertifiedRawMaterialWeightUsedInformation: updatedData,
     });
  };

  return (
    <>
      <FormBuilder
        formConfig={formConfig}
        onSubmit={() => {
          // When Input TC No is NOT "N/A", enforce at least one raw material row
          if (!isInputTcNoNA && (!data || _.isEmpty(data))) {
            notify("Please add certified raw materials details", "error");
            return;
          }
          // Otherwise let the form builder proceed
        }}
        formRef={formRef}
      />
      <Box display="flex" mb={3} justifyContent="space-between" width="100%">
        <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
          Input Certified Raw Material Used Details
        </Typography>
                 <StyledButton
           disabled={isRestrictPopUpSave || !selectedAssociatedProductNo}
           onClick={handleAddNewRow}
           sx={{ mb: 2 }}
         >
           Add Raw Material
         </StyledButton>
      </Box>
      <EditableTable
        data={data}
        columns={columns}
        onSaveRow={handleSaveRow}
        onCellChange={handleCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleEditRow}
        onCancelEdit={handleCancelEdit}
        onDeleteRow={handleDeleteRow}
        enableEditing={!!selectedAssociatedProductNo}
      />
    </>
  );
};

export default InputTcDetails;
